import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import TimeBadge from "@/components/TimeBadge";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, XCircle, ArrowDownLeft, ArrowUpRight, Sparkles } from "lucide-react";

const STATUS_LABEL = {
  escrow: "In escrow",
  delivered: "Delivered — confirm receipt",
  completed: "Completed",
  cancelled: "Cancelled / refunded",
  disputed: "Disputed",
};

const STATUS_STYLE = {
  escrow: "bg-amber-50 text-amber-800 border-amber-200",
  delivered: "bg-blue-50 text-blue-800 border-blue-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
  disputed: "bg-red-50 text-red-700 border-red-200",
};

export default function Transactions() {
  const { user, refresh } = useAuth();
  const [txs, setTxs] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const load = () => api.get("/me/transactions").then(({ data }) => setTxs(data));
  useEffect(() => { load(); }, []);

  const action = async (id, path) => {
    setBusy(id + path);
    setErr("");
    try {
      await api.post(`/marketplace/${path}`, { transaction_id: id });
      await refresh();
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <Layout>
      <div data-testid="transactions-page" className="max-w-5xl mx-auto px-5 md:px-8 py-12">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="overline text-[var(--cm-primary)]">Hours ledger</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 tracking-tight">Your transactions.</h1>
            <p className="text-ink-soft mt-2 max-w-xl">Earnings, spending, escrows, refunds — every hour accounted for.</p>
          </div>
          <div className="card-soft px-6 py-4">
            <span className="overline text-ink-soft">Balance</span>
            <div className="font-display font-bold text-3xl mt-1" data-testid="tx-balance">
              {Number(user?.time_hours_balance ?? 0).toFixed(1)} <span className="text-base font-normal text-ink-soft">hrs</span>
            </div>
          </div>
        </div>

        {err && <div className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">{err}</div>}

        <div className="mt-8 space-y-3">
          {txs.length === 0 ? (
            <div className="card-soft p-12 text-center" data-testid="tx-empty">
              <p className="text-ink-soft">No transactions yet.</p>
            </div>
          ) : (
            txs.map((t) => {
              const isPurchase = t.kind === "purchase";
              const isBuyer = isPurchase && t.buyer_id === user?.id;
              const isSeller = isPurchase && t.seller_id === user?.id;
              const direction = t.kind === "earned" || t.kind === "bonus" || isSeller ? "in" : "out";
              return (
                <div key={t.id} className="card-soft p-5 flex flex-wrap items-center gap-4" data-testid={`tx-${t.id}`}>
                  <span
                    className="h-10 w-10 rounded-full grid place-items-center text-white flex-shrink-0"
                    style={{ background: direction === "in" ? "var(--cm-secondary)" : "var(--cm-primary)" }}
                  >
                    {t.kind === "bonus" ? (
                      <Sparkles className="h-4 w-4" />
                    ) : direction === "in" ? (
                      <ArrowDownLeft className="h-4 w-4" />
                    ) : (
                      <ArrowUpRight className="h-4 w-4" />
                    )}
                  </span>
                  <div className="flex-1 min-w-[180px]">
                    <h3 className="font-display font-semibold leading-tight">
                      {t.description || t.listing_title || "Transaction"}
                    </h3>
                    <p className="text-xs text-ink-soft mt-1">
                      {isPurchase ? (
                        isBuyer ? <>You → {t.seller_name}</> : <>{t.buyer_name} → You</>
                      ) : t.kind === "earned" ? (
                        <>Earned · verified by {t.counterparty_name}</>
                      ) : t.kind === "bonus" ? (
                        <>Welcome bonus</>
                      ) : null}
                    </p>
                  </div>
                  <span className={`pill ${STATUS_STYLE[t.status] || ""}`} data-testid={`tx-status-${t.id}`}>
                    {STATUS_LABEL[t.status] || t.status}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={`font-display font-bold text-lg ${direction === "in" ? "text-[var(--cm-secondary)]" : "text-[var(--cm-primary)]"}`} data-testid={`tx-amount-${t.id}`}>
                      {direction === "in" ? "+" : "-"}{Number(t.hours).toFixed(1)}
                    </span>
                    <TimeBadge hours={t.hours} size="sm" />
                  </div>
                  {isPurchase && t.status === "escrow" && isSeller && (
                    <button
                      data-testid={`tx-mark-delivered-${t.id}`}
                      onClick={() => action(t.id, "mark-delivered")}
                      disabled={busy.startsWith(t.id)}
                      className="btn-outline text-sm py-1.5 px-3 disabled:opacity-60"
                    >
                      Mark delivered
                    </button>
                  )}
                  {isPurchase && (t.status === "escrow" || t.status === "delivered") && isBuyer && (
                    <button
                      data-testid={`tx-confirm-${t.id}`}
                      onClick={() => action(t.id, "confirm-receipt")}
                      disabled={busy.startsWith(t.id)}
                      className="btn-primary text-sm py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-60"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Confirm received
                    </button>
                  )}
                  {isPurchase && (t.status === "escrow" || t.status === "delivered") && (
                    <button
                      data-testid={`tx-cancel-${t.id}`}
                      onClick={() => action(t.id, "cancel")}
                      disabled={busy.startsWith(t.id)}
                      className="btn-outline text-sm py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-60"
                    >
                      <XCircle className="h-3.5 w-3.5" /> Cancel
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
}
